/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { UserProfile } from "@microsoft/shifts-models-data";
import { GraphService } from "sh-services";
import { DataProvider } from "sh-services/dataproviders/DataProvider";
import { UserProfilesStore } from "sh-stores";

import { UserProfileDataProviderParams } from "./UserProfileDataProviderParams";

/**
 * The user profile data provider.
 */
export class UserProfileDataProvider extends DataProvider<UserProfile[]> {
    /**
     * Instantiates a new instance of the UserProfileDataProvider.
     * @param params The parameters.
     */
    constructor(private readonly params: UserProfileDataProviderParams) {
        super();
    }

    /**
     * Gets data from memory or undefined.
     * @returns The data if it's found in memory.
     */
    public async getDataFromMemory(): Promise<UserProfile[] | undefined> {
         const userProfilesInMemory: UserProfile[] = [];
         if (UserProfilesStore().userprofiles?.size > 0) {
            this.params.usersIds.forEach( userId => {
                const userProfile = UserProfilesStore().userprofiles.get(userId);
                if (userProfile) { userProfilesInMemory.push(userProfile); }
            });
         }
         return userProfilesInMemory?.length > 0 ?  userProfilesInMemory :  undefined;
    }

    /**
     * Return data if it's found in the database (otherwise return undefined).
     * @returns The data if it's found in database.
     */
    public async getDataFromDatabase(): Promise<UserProfile[]> {
        const { database, usersIds } = this.params;
        const users = await database.getUsersProfiles(usersIds);

        return users;
    }

    /**
     * Gets data from network call.
     * @returns The data from the network call.
     */
    public async getDataFromNetwork(): Promise<UserProfile[]> {
        const { usersIds } = this.params;

        const users = await GraphService.getUsersProfiles(usersIds);

        return users;
    }

    /**
     * Sets data in memory.
     * @param data The data to set in memory.
     */
    public async setDataInMemory(data: UserProfile[]): Promise<void> {
        this.params.setDataInMemory(data);
    }

    /**
     * Sets data in database.
     * @param data The data to set in database.
     */
    public async setDataInDatabase(data?: UserProfile[]): Promise<void> {
        if (data) {
            await this.params.database.setUsersProfiles(data);
        }
    }

    /**
     * Not supported.
     */
    public async saveDataToNetwork(): Promise<UserProfile[]> {
        return [];
    }
}
